import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
} from '@angular/core'
import { FlexLayoutModule } from '@angular/flex-layout'
import { FormControl, ReactiveFormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatDialog } from '@angular/material/dialog'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatTooltipModule } from '@angular/material/tooltip'
import {
  DirectivesModule,
  EstimateCategoryModel,
  EstimateTemplateCatalogService,
  EstimateTemplateModel,
  FacilityService,
  ProgressIndicatorService,
} from '@mmx/shared'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { EMPTY, Subject } from 'rxjs'
import { catchError, debounceTime, distinctUntilChanged, filter, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators'

import {
  EstimateCategoriesDialogComponent,
  EstimateCategoryDialogResult,
  EstimateTemplateDialogResult,
  EstimateTemplatesDialogComponent,
} from '../estimate-dialogs'

// Signals + inject() here is intentional, not a stylistic miss vs the RxJS siblings —
// this picker owns its full data lifecycle (HTTP, dialogs, post-create refresh) and is
// the team's pilot for the signals pattern. Don't "normalize" it back to RxJS.
@Component({
  selector: 'mmx-estimate-template-menu',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FlexLayoutModule,
    ReactiveFormsModule,
    TranslateModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    DirectivesModule,
  ],
  templateUrl: './estimate-template-menu.component.html',
  styleUrls: ['./estimate-template-menu.component.scss'],
})
export class EstimateTemplateMenuComponent implements OnInit, OnDestroy {
  readonly facilityId = input<string | null>(null)
  readonly templateSelected = output<string>()

  readonly filterCtrl = new FormControl<string>('', { nonNullable: true })

  readonly currentCategoryId = signal<string | null>(null)
  readonly searchTerm = signal<string>('')
  readonly categoriesLoading = signal<boolean>(false)
  readonly templatesLoading = signal<boolean>(false)
  readonly categories = signal<EstimateCategoryModel[]>([])
  readonly templates = signal<EstimateTemplateModel[]>([])

  readonly visibleCategories = computed(() => {
    const parentId = this.currentCategoryId()
    const term = this.searchTerm()
    return this.categories().filter((category) => {
      if ((category.parentId ?? null) !== parentId) {
        return false
      }
      if (!category.isActive) {
        return false
      }
      return !term || (category.name || '').toLowerCase().includes(term)
    })
  })

  readonly visibleTemplates = computed(() => {
    const categoryId = this.currentCategoryId()
    const term = this.searchTerm()
    return this.templates().filter((template) => {
      if ((template.categoryId ?? null) !== categoryId) {
        return false
      }
      return !term || (template.name || '').toLowerCase().includes(term)
    })
  })

  readonly breadcrumbs = computed(() => {
    const categoryId = this.currentCategoryId()
    if (!categoryId) {
      return [] as EstimateCategoryModel[]
    }
    const byId = new Map(this.categories().map((category) => [category.id, category]))
    const trail: EstimateCategoryModel[] = []
    let pointer: string | null = categoryId
    while (pointer) {
      const node = byId.get(pointer)
      if (!node) {
        break
      }
      trail.unshift(node)
      pointer = node.parentId ?? null
    }
    return trail
  })

  private readonly destroy$ = new Subject<void>()

  constructor(
    private readonly dialog: MatDialog,
    private readonly catalog: EstimateTemplateCatalogService,
    private readonly progress: ProgressIndicatorService,
    private readonly translate: TranslateService,
    private readonly facilityService: FacilityService,
  ) {
    // Re-fetch templates when the host's facility changes; categories are facility-independent.
    effect(() => {
      const id = this.facilityId()
      if (id !== undefined) {
        this.loadTemplates()
      }
    })
  }

  ngOnInit(): void {
    this.filterCtrl.valueChanges
      .pipe(
        startWith(this.filterCtrl.value || ''),
        debounceTime(200),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe((value) => {
        this.searchTerm.set(String(value || '').trim().toLowerCase())
      })

    this.loadCategories()
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  selectCategory(category: EstimateCategoryModel | string): void {
    const id = typeof category === 'string' ? category : category.id
    this.currentCategoryId.set(id)
    this.loadTemplates()
  }

  selectBreadcrumb(index: number): void {
    const trail = this.breadcrumbs()
    if (index < 0 || index >= trail.length) {
      return
    }
    this.currentCategoryId.set(trail[index].id)
    this.loadTemplates()
  }

  resetCategory(): void {
    this.currentCategoryId.set(null)
    this.loadTemplates()
  }

  pickTemplate(id: string): void {
    if (id) {
      this.templateSelected.emit(id)
    }
  }

  openNewCategoryDialog(): void {
    const initialParentId = this.currentCategoryId()
    const dialogRef = this.dialog.open(EstimateCategoriesDialogComponent, {
      data: {
        categories: this.categories(),
        initialParentId,
      },
      width: '500px',
      panelClass: 'dialog-form',
      hasBackdrop: false,
      restoreFocus: false,
    })

    dialogRef
      .afterClosed()
      .pipe(
        filter((result): result is EstimateCategoryDialogResult => !!result),
        switchMap((result) => {
          const progress = this.progress.start()
          return this.catalog
            .createOrUpdateCategory({
              name: result.name,
              parentId: result.parentId,
              status: result.status,
            })
            .pipe(
              tap((response) => {
                progress.stop(
                  this.translate.instant('estimate-categories.dialog.status.create-success'),
                )
                const created = new EstimateCategoryModel(response.data)
                this.upsertCategory(created)
                this.loadCategories(true, () => this.loadTemplates(true))
              }),
              catchError((error) => {
                progress.stop(
                  error?.error?.message ||
                    this.translate.instant('estimate-categories.dialog.status.create-error'),
                )
                return EMPTY
              }),
            )
        }),
        takeUntil(this.destroy$),
      )
      .subscribe()
  }

  openNewTemplateDialog(): void {
    const id = this.facilityId()
    if (!id) {
      return
    }
    const facility = this.facilityService.allFacilities.find((f) => f.id === id)
    if (!facility) {
      return
    }

    const dialogRef = this.dialog.open(EstimateTemplatesDialogComponent, {
      data: {
        facility,
        categoryId: this.currentCategoryId(),
      },
      width: '900px',
      panelClass: 'dialog-form',
      hasBackdrop: false,
      restoreFocus: false,
    })

    dialogRef
      .afterClosed()
      .pipe(
        filter((result): result is EstimateTemplateDialogResult => !!result),
        switchMap((result) => {
          const progress = this.progress.start()
          return this.catalog
            .createOrUpdateTemplate({
              name: result.name,
              facilityIds: result.facilityIds,
              categoryId: result.categoryId,
              charges: result.charges,
            })
            .pipe(
              tap(() => {
                progress.stop(
                  this.translate.instant('estimate-templates.dialog.status.create-success'),
                )
                this.loadTemplates(true)
              }),
              catchError((error) => {
                progress.stop(
                  error?.error?.message ||
                    this.translate.instant('estimate-templates.dialog.status.create-error'),
                )
                return EMPTY
              }),
            )
        }),
        takeUntil(this.destroy$),
      )
      .subscribe()
  }

  private loadCategories(forceRefresh = false, onComplete?: () => void): void {
    this.categoriesLoading.set(true)
    this.catalog
      .getCategories({}, forceRefresh)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (categories) => {
          this.categories.set(categories)
          this.categoriesLoading.set(false)
          onComplete?.()
        },
        error: () => {
          this.categoriesLoading.set(false)
          onComplete?.()
        },
      })
  }

  private loadTemplates(forceRefresh = false, onComplete?: () => void): void {
    const id = this.facilityId()
    if (!id) {
      this.templates.set([])
      this.templatesLoading.set(false)
      onComplete?.()
      return
    }
    this.templatesLoading.set(true)
    const categoryId = this.currentCategoryId() ?? 'root'
    this.catalog
      .getTemplates({ categoryId, all: true }, forceRefresh)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (templates) => {
          this.templates.set(
            templates.filter((template) => {
              if (!template.facilityIds?.length) {
                return true
              }
              return template.facilityIds.includes(id)
            }),
          )
          this.templatesLoading.set(false)
          onComplete?.()
        },
        error: () => {
          this.templatesLoading.set(false)
          onComplete?.()
        },
      })
  }

  private upsertCategory(category: EstimateCategoryModel): void {
    const current = this.categories()
    const index = current.findIndex((item) => item.id === category.id)
    const next = [...current]
    if (index >= 0) {
      next[index] = category
    } else {
      next.push(category)
    }
    this.categories.set(next)
  }
}
